// round-outcome.ts — what a round that did NOT settle means for the player's money (SC-61817).
//
// The SDK answers play() with a settled round or with a denial code. The game needs ONE thing from a denial: is it
// CERTAIN that this round moved no money? Checked against the SDK (0.13.0: real-platform-client.ts, real-edge-wire.ts,
// round-error-map.ts), rgs-core-api-gw (browsererrors.go, realplay.go) and rgs-core-runtime (orchestrator.go):
//
//   - refused — CERTAIN: nothing was debited. The game says why and play goes on. Two ways to be certain:
//       the wallet refused the reserve    insufficient_funds, player_locked, bonus_expired: the wallet's own code,
//                                         relayed by the gateway;
//       the gateway refused the request   rate_limited, exposure_cap (SC-62283): its own caps, answered before it
//                                         runs any money leg (realplay.go). Certain because each roll is ONE request
//                                         with its own round id, never sent again (operator.ts).
//   - session — the session is over (session_invalid / session_expired), or it cannot play Dice (SC-66897, SDK 0.30.0:
//     game_not_launched — launched for another game; game_disabled — the casino no longer offers Dice; the gateway's
//     403, refused before any reservation). The SDK raises error.fatal and boot.ts shows the notice that asks for a
//     launch from the casino; the round itself needs no word.
//   - unconfirmed — NOT certain: everything else.
//       round_failed            no usable answer (SDK 0.13.0): the request failed in transit, nothing came back within
//                               the SDK's 16 s, a gateway 5xx (its own 10 s round timeout included), a 2xx that does not
//                               say what the round paid — or the game stopped the SDK (a launch change, SC-61840)
//       round_rejected          a 4xx with a code the SDK does not know, or with none (a proxy's page)
//       round_conflict          a stake-limit refusal (no money), but also a conflict on the resolve leg, after the debit
//       invalid_operation       a malformed request (no money), but an invalid selection is detected after the debit
//       round_void              the round is voided BEFORE its refund is confirmed
//       transaction_not_found   the resolve leg lost a round whose reserve succeeded
//       round_in_progress       a multi-step begin's code; Dice opens no such round, so here it proves nothing
//       anything else           a code this build does not know, a thrown play(), no answer in time
//     The gateway finishes a round on a context detached from the browser's request, so a round the browser never heard
//     back from may have been debited, settled or refunded. The game cannot tell, and must not guess: it stops, and a
//     reload re-reads the balance from the server.
//
// SC-62233 — the platform now SAYS it (rgs-core-api-gw#166, SDK 0.26.0 DeniedResult.moneyMoved): "no" is proof that the
// request was refused before its money leg (or the leg refused with an answer that proves it), whatever the code; any
// other stated value proves nothing (the SDK turns such a refusal into round_failed), whatever the code. With no flag —
// a gateway before #166 (STG/PROD until their release has it), the SDK's own refusals — the lists below decide, plus
// the player stake limit (SDK 0.26.0: refused before any debit). Each roll is ONE request with its own round id, never
// sent again, so what the platform says of the request is said of the round.

export type RoundDenial =
  | { readonly kind: "refused"; readonly code: string; readonly message: string }
  | { readonly kind: "session"; readonly code: string }
  | { readonly kind: "unconfirmed"; readonly code: string };

// What the player is told, in the game's own voice (the same short form as its "Not enough balance" / "Enter a
// stake"): never the server's text.
const REFUSED: ReadonlyMap<string, string> = new Map([
  ["insufficient_funds", "Not enough balance"],
  ["player_locked", "Betting is not available on your account"],
  ["bonus_expired", "Your bonus has expired"],
  // The gateway's caps (SC-62283). The rate cap passes on its own; the session's wagering cap only grows, and a
  // relaunch from the casino starts a new session. Kept short: the game's rail does not wrap, and at 320px of width
  // it has room for ~250px of this text (the smoke checks that neither message is cut).
  ["rate_limited", "Please wait a moment before your next bet"],
  ["exposure_cap", "Session limit reached. Relaunch from the casino"],
  // SC-62233: the player's exposure cap, refused before any debit; a smaller stake, or one once open rounds settle,
  // may pass.
  ["player_stake_limit_exceeded", "Stake limit reached. Try a smaller bet or wait"],
  // SC-65843 (pf-ticket-v1, SDK 0.21+): both decided before any debit. The ticket could not be used — the SDK already
  // holds the next one, so the player may bet again; or the terms changed after the player saw them — the SDK already
  // holds the new ones (the Provably fair door shows them), so the player looks and decides again. A run stops on
  // either (the vendored autobet stops on every refused roll), and nothing is sent again on the player's behalf.
  ["pf_ticket_refused", "Bet not placed. Please bet again"],
  ["accepted_terms_stale", "The terms changed. Check them and bet again"],
  // SC-64981 (SDK 0.31+): the stake's best outcome is more than the round's max win can pay — refused before any debit. The
  // same words as the check made before a roll is sent (operator.ts STAKE_ABOVE_MAX_MESSAGE); when the RGS states the max
  // stake of the selection the driver says it instead, and the board clamps to it.
  ["max_win_exceeded", "Above the max stake for this selection. Check the stake and roll again"],
  // SC-64979 (SDK 0.31+): a selection the product does not offer — refused before any debit. The game offers only its
  // product's selections, so this is a server with other terms than the board's; the player picks again.
  ["selection_not_offered", "This selection is not offered. Choose another"],
]);

/** A refusal the platform proved moved nothing, with no reason of its own above. */
const NOTHING_CHARGED = "Bet not placed. Nothing was charged";

const SESSION_OVER: ReadonlySet<string> = new Set(["session_invalid", "session_expired", "game_not_launched", "game_disabled"]);

/** The code shown for an outcome with no usable code of its own (a thrown play(), a non-string code). */
export const UNCONFIRMED = "round_unconfirmed";
/** No answer within the game's own deadline — a backstop: since SDK 0.13.0 a REAL play() resolves `round_failed`
 *  within about 16 s on its own, so this is reached only if the SDK itself never answers. */
export const TIMED_OUT = "round_timeout";

/** A stable code is short and lower-case; anything else (server text, an object) is never shown. */
const printable = (code: unknown): string => (typeof code === "string" && /^[a-z_]{1,40}$/.test(code) ? code : UNCONFIRMED);

export function classifyDenial(code: unknown, moneyMoved?: unknown): RoundDenial {
  const shown = printable(code);
  if (shown === UNCONFIRMED) return { kind: "unconfirmed", code: UNCONFIRMED }; // no code the game can say
  if (SESSION_OVER.has(shown)) return { kind: "session", code: shown };
  const message = REFUSED.get(shown);
  if (moneyMoved === "no") return { kind: "refused", code: shown, message: message ?? NOTHING_CHARGED };
  if (moneyMoved === undefined && message !== undefined) return { kind: "refused", code: shown, message };
  return { kind: "unconfirmed", code: shown };
}
